import React, { useState, useEffect } from 'react';
import {
  TextField, Button, Stack, Grid, Typography, Box, FormControl, InputLabel,
  Select, MenuItem, Divider, Autocomplete,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Event as EventIcon,
} from '@mui/icons-material';

const APPOINTMENT_TYPES = [
  'General Checkup',
  'Follow-up',
  'Consultation',
  'Emergency',
  'Surgery',
  'Lab Work',
  'Imaging',
  'Physical Therapy',
  'Mental Health',
  'Vaccination',
  'Other',
];

const APPOINTMENT_STATUSES = [
  'Scheduled',
  'Confirmed',
  'In Progress',
  'Completed',
  'Cancelled',
  'No Show',
];

function AppointmentForm({
  appointment = null, onSave, onCancel, loading = false, mode = 'add', patients = [],
}) {
  const [form, setForm] = useState({
    patientId: '',
    patientName: '',
    date: '',
    startTime: '',
    endTime: '',
    duration: 30,
    appointmentType: 'General Checkup',
    provider: '',
    location: '',
    reason: '',
    status: 'Scheduled',
    notes: '',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (appointment) {
      setForm({
        patientId: appointment.patientId || '',
        patientName: appointment.patientName || '',
        date: appointment.date ? appointment.date.split('T')[0] : '',
        startTime: appointment.startTime || '',
        endTime: appointment.endTime || '',
        duration: appointment.duration || 30,
        appointmentType: appointment.appointmentType || 'General Checkup',
        provider: appointment.provider || '',
        location: appointment.location || '',
        reason: appointment.reason || '',
        status: appointment.status || 'Scheduled',
        notes: appointment.notes || '',
      });
    }
  }, [appointment]);

  const validateForm = () => {
    const newErrors = {};
    if (!form.patientName.trim()) newErrors.patientName = 'Patient name is required';
    if (!form.date) newErrors.date = 'Date is required';
    if (!form.startTime) newErrors.startTime = 'Start time is required';
    if (!form.provider.trim()) newErrors.provider = 'Provider is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    onSave(form);
  };

  const handleInputChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handlePatientSelect = (event, value) => {
    if (value) {
      handleInputChange('patientId', value._id);
      handleInputChange('patientName', `${value.firstName} ${value.lastName}`);
    } else {
      handleInputChange('patientId', '');
      handleInputChange('patientName', '');
    }
  };

  return (
    <Box component="form" onSubmit={handleSave} sx={{ maxWidth: 900, mx: 'auto' }}>
      <Stack spacing={3}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={2}>
          <EventIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h5" fontWeight={700}>
            {mode === 'edit' ? 'Edit Appointment' : 'Add New Appointment'}
          </Typography>
        </Stack>
        <Divider />

        {/* Patient Selection */}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom fontWeight={700}>
              Patient Information
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {patients.length > 0 ? (
              <Autocomplete
                options={patients}
                getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                value={patients.find((p) => p._id === form.patientId) || null}
                onChange={handlePatientSelect}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Patient"
                    required
                    error={Boolean(errors.patientName)}
                    helperText={errors.patientName}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box>
                      <Typography variant="body2">
                        {option.firstName}
                        {' '}
                        {option.lastName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.email || option.phone}
                      </Typography>
                    </Box>
                  </li>
                )}
              />
            ) : (
              <TextField
                label="Patient Name"
                fullWidth
                required
                value={form.patientName}
                onChange={(e) => handleInputChange('patientName', e.target.value)}
                error={Boolean(errors.patientName)}
                helperText={errors.patientName || 'No patients available - enter name manually'}
              />
            )}
          </Grid>
        </Grid>

        {/* Date and Time */}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom fontWeight={700}>
              Date & Time
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Date"
              type="date"
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              value={form.date}
              onChange={(e) => handleInputChange('date', e.target.value)}
              error={Boolean(errors.date)}
              helperText={errors.date}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Start Time"
              type="time"
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              value={form.startTime}
              onChange={(e) => handleInputChange('startTime', e.target.value)}
              error={Boolean(errors.startTime)}
              helperText={errors.startTime}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="End Time"
              type="time"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.endTime}
              onChange={(e) => handleInputChange('endTime', e.target.value)}
            />
          </Grid>
        </Grid>

        {/* Appointment Details */}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom fontWeight={700}>
              Appointment Details
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Appointment Type</InputLabel>
              <Select
                value={form.appointmentType}
                onChange={(e) => handleInputChange('appointmentType', e.target.value)}
                label="Appointment Type"
              >
                {APPOINTMENT_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Status</InputLabel>
              <Select
                value={form.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                label="Status"
              >
                {APPOINTMENT_STATUSES.map((status) => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Provider"
              fullWidth
              required
              value={form.provider}
              onChange={(e) => handleInputChange('provider', e.target.value)}
              error={Boolean(errors.provider)}
              helperText={errors.provider}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Location"
              fullWidth
              value={form.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
            />
          </Grid>
        </Grid>

        {/* Reason and Notes */}
        <TextField
          label="Reason for Visit"
          multiline
          rows={2}
          fullWidth
          value={form.reason}
          onChange={(e) => handleInputChange('reason', e.target.value)}
          placeholder="What is the reason for this appointment?"
        />
        <TextField
          label="Notes"
          multiline
          rows={3}
          fullWidth
          value={form.notes}
          onChange={(e) => handleInputChange('notes', e.target.value)}
          placeholder="Additional notes about this appointment..."
        />

        {/* Action Buttons */}
        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ pt: 2 }}>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={loading}
          >
            {loading ? 'Saving...' : mode === 'edit' ? 'Update Appointment' : 'Create Appointment'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

export default AppointmentForm;
